import React,{ useState } from "react";
import {Checkbox, FormControlLabel, TextField, Typography} from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Enums from "../../../General/Enums";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from "dayjs";
import {DECISION_TYPES} from "../../../CV/DecisionTypes";
import {updateCandidateState} from "../../../../utilities/HrAPI";
import keycloak from "../../../../Keycloak";
import {toast} from "react-toastify";
import {DesktopDatePicker} from "@mui/x-date-pickers";

const DecisionModal = ({heading, modalSettings, setCandidate, candidate, cvId, setShowDecisionModal}) => {

    const [selectedValue, setSelectedValue] = useState("");
    const [selectedDateTime, setSelectedDateTime] = useState(dayjs());
    const [additionalInfo, setAdditionalInfo] = useState("");
    const [checkedStates, setCheckedStates] = useState({});
    const [locale] = useState('en-gb');

    const style = {
        "& label.Mui-focused": {
            color: "#FF6600",
        },
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: "#FF6600",
            },
        },
        "& .MuiCheckbox-root": {
            color: '#FF6600',
            '&.Mui-checked': {
                color: '#FF6600',
            },
        },
    };

    const closeModal = () => setShowDecisionModal(false);

    const getDescription = (decision) => DECISION_TYPES[decision]?.label || DECISION_TYPES.NO_DECISION.label;

    const getDetailsBasedOnType = (type) => {
        switch (type) {
            case 1: return selectedValue === "Other" ? additionalInfo : selectedValue;
            case 2: return modalSettings.options.filter(option => checkedStates[option.value]).map(option => option.label).join(", ");
            case 3: return selectedDateTime.toISOString();
            default: return "";
        }
    }

    const showSaveSuccess = (string) => {
        toast.success(string, {
            position: toast.POSITION.TOP_LEFT,
            style: {
                backgroundColor: "#49c96d",
                width: "650px",
                color: "#FFFFFF",
                left: "500px",
                theme: "colored",
            }
        });
    };

    const saveModal = async () => {
        const result = {
            name: heading || 'NO_DECISION',
            description: getDescription(heading),
            details: getDetailsBasedOnType(modalSettings.type)
        };
        candidate.status = result;
        setCandidate(prev => ({ ...prev, status: result }));

        try {
            await updateCandidateState(cvId, candidate, keycloak.token);
            showSaveSuccess(`${result.description} email sent successfully`)
            console.log("Candidate state updated successfully.");
        } catch (error) {
            console.error("Could not update candidate state", error);
        }

        closeModal();
    }

    const renderModalContent = () => {
        switch (modalSettings.type) {
            case 1:
                return renderSelect();
            case 2:
                return renderCheckbox();
            case 3:
                return renderDatePicker();
            default:
                return null;
        }
    };

    const renderSelect = () => (
        <TextField label={modalSettings.subHeading}
                   data-testid={"select-decision-reason"}
                   select
                   sx={style}
                   value={selectedValue}
                   onChange={(e) => setSelectedValue(e.target.value)}
                   style={{ width: "60%" }}>
            {modalSettings.options.map((option) => (
                <MenuItem key={option.value} value={option.label}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );

    const renderCheckbox = () => (
        <Grid container direction={"row"} spacing={1} padding={1}>
            {modalSettings.options.map((option) => (
                <Grid item xs={12} key={option.value}>
                    <FormControlLabel
                        sx={style}
                        control={
                            <Checkbox data-testid={`select-decision-checkbox-${option.value}`}
                                checked={checkedStates[option.value]}
                                onChange={(e) => handleCheckboxChange(e, option.value)}
                            />
                        }
                        label={option.label}
                    />
                </Grid>
            ))}
        </Grid>
    );

    const renderDatePicker = () => (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
                <DemoContainer components={['DatePicker', 'TimePicker']}>
                <DesktopDatePicker
                            label={"Date *"}
                            value={selectedDateTime}
                            onChange={handleDateChange}
                            format="DD/MM/YYYY"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth={false}
                                    error={false}
                                    inputProps={{ ...params.inputProps, "data-testid": "select-decision-date" }}
                                    style={{ width: "20%" }}
                                />
                            )}
                            sx={style}
                            style={{ width: "20%" }}
                            minDate={dayjs()}
                />
                <TimePicker label={"Time *"}
                            value={selectedDateTime}
                            onChange={handleTimeChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth={false}
                                    error={false}
                                    inputProps={{ ...params.inputProps, "data-testid": "select-decision-time" }}
                                    style={{ width: "20%" }}
                                />
                            )}
                            sx={style}
                />
                </DemoContainer>
            </LocalizationProvider>

    );

    const handleDateChange = (newDate) => {
        const newDateTime = newDate.hour(selectedDateTime.hour()).minute(selectedDateTime.minute());
        setSelectedDateTime(newDateTime);
    };

    const handleTimeChange = (newTime) => {
        const newDateTime = selectedDateTime.hour(newTime.hour()).minute(newTime.minute());
        setSelectedDateTime(newDateTime);
    };

    const handleCheckboxChange = (event, value) => {
        setCheckedStates(prev => ({
            ...prev,
            [value]: event.target.checked
        }));
    };

    return (
        <Box sx={Enums.modalStyle} data-testid={"decisionModalBox"}>
            <Grid container direction={"row"} padding={2} spacing={2}>
                <Grid item xs={12}>
                    <Typography id="modal-modal-title" variant="h4" component="h2" data-testid={"decisionModalHeading"}>
                        {DECISION_TYPES[heading].label}
                    </Typography>
                </Grid>
                <Grid item xs={12} hidden={modalSettings.type === 1 || modalSettings.type === 3}>
                    <Typography id="modal-modal-title" variant="h6" component="h2">
                        {modalSettings.subHeading}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    {renderModalContent()}
                </Grid>
                {modalSettings.type !== 3 && (
                    <Grid item xs={12}>
                        <TextField multiline
                                   hidden={selectedValue !== "Other"}
                                   minRows={2}
                                   sx={{ ...style, width: "60%" }}
                                   label="Insert comment"
                                   value={additionalInfo}
                                   onChange={(e) => setAdditionalInfo(e.target.value)}
                                   data-testid="select-decision-comment"
                        />
                    </Grid>
                )}
                <Grid item xs={10}>
                    <Button variant="outlined" data-testid={"cancel"} onClick={closeModal}
                            style={{
                                float: 'right',
                                width: '18%',
                                height: '40px',
                                fontFamily: 'Poppins',
                                color: '#FF6600',
                                borderColor: '#FF6600'
                            }}
                    >
                        Cancel
                    </Button>
                </Grid>
                <Grid item xs={2}>
                    <Button data-testid={"save"}
                            onClick={saveModal}
                            style={{
                                float: "right",
                                backgroundColor: '#FF6600',
                                width: '100%',
                                height: '40px',
                                fontFamily: 'Poppins',
                                color: '#FFFFFF',
                                marginLeft: 'auto',
                            }}
                    >
                        Save
                    </Button>
                </Grid>
            </Grid>
        </Box>
    )
}

export default DecisionModal;